import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Check, ChevronDown, Copy, ExternalLink, ListTree, Loader2, Pencil, Truck, X } from "lucide-react";
import { FulfillmentEvent, Order } from "../../SalesPage/SalesPageInterface";
import { useAuth } from "../../../context/AuthContext";
import { dictionary, Language } from "../../../utils/dictionary";
import { formatDateInZone, getCurrencySymbol } from "../../../utils/formatters";
import { getOrderDisplayCurrencyCode } from "../../../utils/orderDisplayTotals";
import { PROVIDER_NAMES, fulfillmentStatusLabel, owningOrderProvider } from "../../../utils/fulfillment";
import { handleApi } from "../../../utils/handleApi";
import { useFeatureGates } from "../../../hooks/useFeatureGates";
import { setManualFulfillmentStatus } from "../../../services/orders/orderAxios";

type Tone = "success" | "info" | "warning" | "danger" | "neutral";

const STATUS_META: Record<string, { tone: Tone; rank: number }> = {
  PENDING: { tone: "neutral", rank: 0 },
  GUIDE_GENERATED: { tone: "info", rank: 10 },
  PICKED_UP: { tone: "info", rank: 20 },
  IN_TRANSIT: { tone: "info", rank: 30 },
  INCIDENT: { tone: "warning", rank: 35 },
  OUT_FOR_DELIVERY: { tone: "info", rank: 40 },
  DELIVERED: { tone: "success", rank: 100 },
  RETURNED: { tone: "warning", rank: 90 },
  CANCELED: { tone: "danger", rank: 90 },
};

const NEGATIVE_TERMINAL = new Set(["RETURNED", "CANCELED"]);

const STEPS: { labelKey: string; threshold: number }[] = [
  { labelKey: "delivery-step-confirmed", threshold: 0 },
  { labelKey: "delivery-step-guide", threshold: 10 },
  { labelKey: "delivery-step-transit", threshold: 20 },
  { labelKey: "delivery-step-out", threshold: 40 },
  { labelKey: "delivery-step-delivered", threshold: 100 },
];

const BADGE_TONE: Record<Tone, string> = {
  success: "bg-emerald-50 text-emerald-700",
  info: "bg-blue-50 text-blue-700",
  warning: "bg-amber-50 text-amber-700",
  danger: "bg-red-50 text-red-700",
  neutral: "bg-gray-100 text-gray-600",
};

const BANNER_TONE: Record<string, string> = {
  warning: "bg-amber-50 text-amber-700",
  danger: "bg-red-50 text-red-700",
};

const SOURCE_KEYS: Record<string, string> = {
  webhook: "delivery-source-webhook",
  poll: "delivery-source-poll",
  manual: "delivery-source-manual",
};

const deriveEvents = (order: Order): FulfillmentEvent[] => {
  if (order.fulfillment_events && order.fulfillment_events.length > 0) {
    return order.fulfillment_events;
  }
  if (!order.fulfillment_status) return [];
  return [
    {
      mapped_status: order.fulfillment_status,
      raw_status: order.fulfillment_raw_status,
      carrier: order.carrier,
      event_at: order.fulfillment_event_at || order.delivered_at || null,
    },
  ];
};

const Timeline = ({
  events,
  showSource,
  language,
  timezone,
}: {
  events: FulfillmentEvent[];
  showSource?: boolean;
  language: Language;
  timezone: string;
}) => {
  if (events.length === 0) {
    return <p className="text-xs text-gray-400">{dictionary["delivery-no-events"][language]}</p>;
  }
  return (
    <ol className="flex flex-col">
      {events.map((event, index) => {
        const isCurrent = index === 0;
        const isLast = index === events.length - 1;
        return (
          <li key={`${event.event_at ?? "e"}-${index}`} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span
                className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${
                  isCurrent ? "bg-emerald-500" : "bg-gray-300"
                }`}
              />
              {!isLast && <span className="w-px flex-1 bg-gray-200" />}
            </div>
            <div className={isLast ? "" : "pb-3"}>
              <p className={`text-sm ${isCurrent ? "font-semibold text-gray-900" : "text-gray-700"}`}>
                {fulfillmentStatusLabel(event.mapped_status, language)}
                {showSource && event.source && SOURCE_KEYS[event.source] && (
                  <span className="ml-1.5 text-xs font-normal text-gray-400">
                    · {dictionary[SOURCE_KEYS[event.source]][language]}
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-400">
                {formatDateInZone(event.event_at, timezone, "dd/MM/yyyy · HH:mm")}
                {event.carrier ? ` · ${event.carrier}` : ""}
              </p>
              {showSource && event.raw_status && (
                <p className="mt-1 rounded-md bg-gray-50 px-2 py-1 font-mono text-[11px] text-gray-500">
                  {event.raw_status}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

// Orden de los estados en el menú manual (flujo + terminales).
const MANUAL_STATUS_ORDER = [
  "PENDING",
  "GUIDE_GENERATED",
  "PICKED_UP",
  "IN_TRANSIT",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "INCIDENT",
  "RETURNED",
  "CANCELED",
];

const DOT_TONE: Record<Tone, string> = {
  success: "bg-emerald-500",
  info: "bg-blue-500",
  warning: "bg-amber-500",
  danger: "bg-red-500",
  neutral: "bg-gray-400",
};

const ManualStatusMenu = ({
  orderId,
  current,
  language,
  onChanged,
}: {
  orderId: number;
  current: string;
  language: Language;
  onChanged?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  const apply = async (status: string) => {
    if (saving || status === current) {
      setOpen(false);
      return;
    }
    setSaving(true);
    const { error } = await handleApi(setManualFulfillmentStatus(orderId, status));
    setSaving(false);
    setOpen(false);
    if (!error) onChanged?.();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={saving}
        className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Pencil className="h-3 w-3" />}
        {dictionary["delivery-change-status"][language]}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-52 rounded-xl border border-gray-100 bg-white py-1 shadow-lg">
          {MANUAL_STATUS_ORDER.map((status) => {
            const tone = STATUS_META[status]?.tone ?? "neutral";
            const isCurrent = status === current;
            return (
              <button
                key={status}
                type="button"
                onClick={() => apply(status)}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${
                  isCurrent ? "font-semibold text-gray-900" : "text-gray-700"
                }`}
              >
                <span className={`h-2 w-2 shrink-0 rounded-full ${DOT_TONE[tone]}`} />
                {fulfillmentStatusLabel(status, language)}
                {isCurrent && <Check className="ml-auto h-3.5 w-3.5 text-emerald-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const OrderDeliveryCard = ({
  order,
  variant = "compact",
  className = "",
  style,
  onStatusChanged,
}: {
  order: Order;
  variant?: "compact" | "inline";
  className?: string;
  style?: React.CSSProperties;
  onStatusChanged?: () => void;
}) => {
  const { language, timezone } = useAuth();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<number>();

  useEffect(() => () => window.clearTimeout(copiedTimerRef.current), []);

  // Escape cierra SOLO el modal de historial (en captura, antes del listener del drawer).
  useEffect(() => {
    if (!historyOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setHistoryOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [historyOpen]);

  const gates = useFeatureGates();
  const manualEnabled = gates["orders_status_manual"] === true;

  // Con el gate manual encendido la tarjeta se muestra siempre (hay que poder asignar estado
  // a mano). Apagado, vuelve al comportamiento original: solo con datos de proveedor.
  if (!order.fulfillment_provider && !manualEnabled) return null;

  const owningProvider = owningOrderProvider(order);
  const status = order.fulfillment_status ?? "PENDING";
  const meta = STATUS_META[status] ?? { tone: "neutral" as Tone, rank: 0 };
  const events = deriveEvents(order);

  // En cancelada/devuelta la barra muestra hasta dónde llegó REALMENTE el envío
  // (máximo estado no terminal del historial), no el rank del estado terminal.
  const progressRank = NEGATIVE_TERMINAL.has(status)
    ? Math.max(
        0,
        ...events
          .filter((e) => e.mapped_status && !NEGATIVE_TERMINAL.has(e.mapped_status))
          .map((e) => STATUS_META[e.mapped_status ?? ""]?.rank ?? 0),
      )
    : meta.rank;

  const currencySymbol = getCurrencySymbol(getOrderDisplayCurrencyCode(order));
  const shippingCost =
    order.shipping_cost === null || order.shipping_cost === undefined
      ? null
      : `${currencySymbol}${Number(order.shipping_cost).toLocaleString("es-CO", { maximumFractionDigits: 0 })}`;

  const handleCopy = () => {
    if (!order.guide_number) return;
    navigator.clipboard?.writeText(order.guide_number).then(() => {
      setCopied(true);
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div
      className={`rounded-2xl border border-gray-100 bg-white p-5 shadow-sm ${className}`}
      style={style}
    >
      <div className="mb-4 flex items-center gap-2">
        <Truck className="h-4 w-4 text-gray-400" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          {dictionary["delivery-card-title"][language]}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${BADGE_TONE[meta.tone]}`}>
            {fulfillmentStatusLabel(status, language)}
          </span>
          {owningProvider ? (
            <span className="text-xs text-gray-400">
              {dictionary["delivery-managed-by-provider"][language].replace(
                "{provider}",
                PROVIDER_NAMES[owningProvider],
              )}
            </span>
          ) : manualEnabled ? (
            <ManualStatusMenu
              orderId={order.id}
              current={status}
              language={language}
              onChanged={onStatusChanged}
            />
          ) : null}
        </div>
      </div>

      {(meta.tone === "danger" || meta.tone === "warning") && (
        <div className={`mb-4 flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${BANNER_TONE[meta.tone]}`}>
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {status === "CANCELED"
            ? dictionary["delivery-banner-canceled"][language]
            : status === "RETURNED"
            ? dictionary["delivery-banner-returned"][language]
            : dictionary["delivery-banner-incident"][language]}
        </div>
      )}

      <div className="mb-4 flex items-start justify-between">
        {STEPS.map((step, index) => {
          const done = progressRank >= step.threshold;
          const isLast = index === STEPS.length - 1;
          const nextReached = !isLast && progressRank >= STEPS[index + 1].threshold;
          return (
            <Fragment key={step.labelKey}>
              <div className="flex flex-1 flex-col items-center text-center">
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full ${
                    done ? "bg-emerald-100 text-emerald-700" : "border border-gray-200 text-gray-300"
                  }`}
                >
                  {done ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                  )}
                </span>
                <span className={`mt-1.5 text-[11px] ${done ? "text-gray-600" : "text-gray-400"}`}>
                  {dictionary[step.labelKey][language]}
                </span>
              </div>
              {!isLast && (
                <span className={`mt-3 h-0.5 flex-1 ${nextReached ? "bg-emerald-400" : "bg-gray-200"}`} />
              )}
            </Fragment>
          );
        })}
      </div>

      <div className="grid grid-cols-3 gap-3 border-t border-gray-100 py-3 text-sm">
        <div>
          <p className="text-[11px] text-gray-400">{dictionary["delivery-carrier"][language]}</p>
          <p className="text-gray-900">{order.carrier || "—"}</p>
        </div>
        <div>
          <p className="text-[11px] text-gray-400">{dictionary["delivery-guide"][language]}</p>
          {order.guide_number ? (
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1 font-mono text-gray-900 hover:text-blue-600"
              title={dictionary["delivery-guide"][language]}
            >
              {order.guide_number}
              <Copy className="h-3 w-3 text-gray-400" />
            </button>
          ) : (
            <p className="text-gray-900">—</p>
          )}
          {copied && <span className="text-[11px] text-emerald-600">{dictionary["delivery-copied"][language]}</span>}
        </div>
        <div>
          <p className="text-[11px] text-gray-400">{dictionary["delivery-shipping-cost"][language]}</p>
          <p className="text-gray-900">{shippingCost ?? "—"}</p>
        </div>
      </div>

      {variant === "inline" ? (
        <div className="border-t border-gray-100 pt-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-500">{dictionary["delivery-history"][language]}</p>
            {order.tracking_url && (
              <a
                href={order.tracking_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
              >
                {dictionary["delivery-view-tracking"][language]} <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          <Timeline events={events} showSource language={language} timezone={timezone} />
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
          >
            <ListTree className="h-4 w-4" /> {dictionary["delivery-view-history"][language]}
          </button>
          {order.tracking_url && (
            <a
              href={order.tracking_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
            >
              <ExternalLink className="h-4 w-4" /> {dictionary["delivery-view-tracking"][language]}
            </a>
          )}
        </div>
      )}

      {historyOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 p-4"
            onClick={() => setHistoryOpen(false)}
          >
            <div
              className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between">
                <span className="text-base font-semibold text-gray-900">
                  {dictionary["delivery-history-title"][language]}
                </span>
                <button
                  type="button"
                  onClick={() => setHistoryOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-900"
                  aria-label={dictionary["delivery-history-title"][language]}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <Timeline events={events} showSource language={language} timezone={timezone} />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
